import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import { sliderBottom } from "d3-simple-slider";
import Loading from "../../common/components/loading/loading";
import {
  appendSignedVerticalAreaGradients,
  buildTooltipHtml,
  createTooltipHandlers,
  formatTooltipCurrency,
  formatTooltipDate,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const HistoricalSettlementsGraph = ({
  data,
  title,
  isLoading,
  initialStartDate,
  initialEndDate,
}) => {
  const svgRef = useRef();
  const sliderRef = useRef();
  const tooltipRef = useRef(null);

  const getNumericExtent = (values, fallback = [0, 1]) => {
    const finiteValues = values.filter((value) => Number.isFinite(value));

    if (!finiteValues.length) {
      return fallback;
    }

    const [min, max] = d3.extent(finiteValues);

    if (min === max) {
      const padding = min === 0 ? 1 : Math.abs(min) * 0.1 || 1;
      return [min - padding, max + padding];
    }

    return [min, max];
  };
  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    if (!svg.node()) {
      return;
    }

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight - 20;
    const margin = { top: 30, right: 10, bottom: 50, left: 50 };

    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none")
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const normalizedData = data
      .map((row) => ({
        ...row,
        marketDate: new Date(row.marketDate),
        settlePrice: Number(row.settlePrice),
      }))
      .sort((a, b) => a.marketDate - b.marketDate);

    const groupedData = d3.group(normalizedData, (d) => d.termName);
    const sortedKeys = Array.from(groupedData.keys()).sort((a, b) =>
      String(a).localeCompare(String(b)),
    );

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(normalizedData, (d) => d.marketDate))
      .range([0, innerWidth]);

    const yScale = d3
      .scaleLinear()
      .domain(
        getNumericExtent(normalizedData.map((d) => d.settlePrice)),
      )
      .nice()
      .range([innerHeight, 0]);

    const line = d3
      .line()
      .defined((d) => Number.isFinite(d.settlePrice))
      .x((d) => xScale(d.marketDate))
      .y((d) => yScale(d.settlePrice))
      .curve(d3.curveMonotoneX);

    const colorScale = d3.scaleOrdinal(d3.schemeCategory10).domain(sortedKeys);

    const xAxis = chartSvg
      .append("g")
      .attr("class", "x-axis d3-chart-axis")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale));

    const yAxis = chartSvg
      .append("g")
      .attr("class", "y-axis d3-chart-axis")
      .call(d3.axisLeft(yScale).tickFormat((d) => `$${d.toFixed(2)}`));

    const gridlines = chartSvg
      .append("g")
      .attr("class", "grid d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    const linesGroup = chartSvg.append("g").attr("class", "lines");
    const seriesPaths = new Map();

    sortedKeys.forEach((key) => {
      const seriesData = groupedData.get(key) ?? [];
      const color = colorScale(key);
      const gradients = appendSignedVerticalAreaGradients({
        chartSvg,
        idPrefix: `historical-settlements-${String(key).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`,
        color,
      });
      const area = d3
        .area()
        .defined((d) => Number.isFinite(d.settlePrice))
        .x((d) => xScale(d.marketDate))
        .y0(innerHeight)
        .y1((d) => yScale(d.settlePrice))
        .curve(d3.curveMonotoneX);

      const areaPath = areaLayer
        .append("path")
        .datum(seriesData)
        .attr("class", "d3-chart-area dashboard-area-highlight")
        .attr("fill", `url(#${gradients.positiveGradientId})`)
        .attr("d", area);

      const linePath = linesGroup
        .append("path")
        .datum(seriesData)
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 2)
        .attr("d", line);

      seriesPaths.set(key, {
        area,
        areaPath,
        linePath,
      });
    });

    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text(title)
      .attr("class", "d3-chart-title");

    const legendSpacing = 100;
    const legendLineLength = 20;

    const legend = chartSvg
      .append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", 10)
      .attr("text-anchor", "middle")
      .attr(
        "transform",
        `translate(${innerWidth / 2}, ${-margin.top / 2 + 10})`,
      )
      .selectAll("g")
      .data(sortedKeys)
      .enter()
      .append("g")
      .attr(
        "transform",
        (d, i) =>
          `translate(${i * legendSpacing - ((sortedKeys.length - 1) * legendSpacing) / 2}, 0)`,
      );

    legend
      .append("line")
      .attr("x1", -legendLineLength / 2)
      .attr("x2", legendLineLength / 2)
      .attr("stroke", (d) => colorScale(d))
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", legendLineLength * 2 - 5)
      .attr("y", 3)
      .attr("class", "d3-chart-legend-label")
      .text((d) => d);

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });
    let currentSeriesByKey = new Map(
      sortedKeys.map((key) => [key, groupedData.get(key) ?? []]),
    );
    let tooltipDates = Array.from(
      new Set(normalizedData.map((d) => +d.marketDate)),
      (value) => ({ marketDate: new Date(value) }),
    ).sort((a, b) => a.marketDate - b.marketDate);

    const tooltipBisector = d3.bisector((d) => d.marketDate).center;
    const getNearestTooltipDatum = (event) => {
      if (!tooltipDates.length) {
        return null;
      }

      const [pointerX] = d3.pointer(event, chartSvg.node());
      const hoveredValue = xScale.invert(pointerX);
      const index = tooltipBisector(tooltipDates, hoveredValue);
      return tooltipDates[index] ?? null;
    };

    chartSvg
      .append("rect")
      .attr("class", "d3-chart-hover-overlay")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .attr("fill", "#ffffff")
      .attr("fill-opacity", 0.001)
      .style("pointer-events", "all")
      .raise()
      .on("mouseover", (event) => {
        const datum = getNearestTooltipDatum(event);
        if (!datum) {
          hideTooltip();
          return;
        }

        showTooltip(
          event,
          buildTooltipHtml({
            title: formatTooltipDate(datum.marketDate, "%Y-%m-%d"),
            rows: sortedKeys.map((key) => {
              const point = currentSeriesByKey
                .get(key)
                ?.find((entry) => +entry.marketDate === +datum.marketDate);
              return {
                label: key,
                value: formatTooltipCurrency(point?.settlePrice, 2),
                color: colorScale(key),
              };
            }),
          }),
        );
      })
      .on("mousemove", (event) => {
        const datum = getNearestTooltipDatum(event);
        if (!datum) {
          hideTooltip();
          return;
        }

        showTooltip(
          event,
          buildTooltipHtml({
            title: formatTooltipDate(datum.marketDate, "%Y-%m-%d"),
            rows: sortedKeys.map((key) => {
              const point = currentSeriesByKey
                .get(key)
                ?.find((entry) => +entry.marketDate === +datum.marketDate);
              return {
                label: key,
                value: formatTooltipCurrency(point?.settlePrice, 2),
                color: colorScale(key),
              };
            }),
          }),
        );
      })
      .on("mouseout", hideTooltip);

    const sliderSvg = d3
      .select(sliderRef.current)
      .attr("width", parentWidth)
      .attr("preserveAspectRatio", "none")
      .attr("class", "no-print")
      .attr("transform", "translate(0,-20)");

    sliderSvg.selectAll("*").remove();

    const sliderWidth = innerWidth;

    const updateChart = (range) => {
      const [startDate, endDate] = range;
      const filteredData = Array.from(groupedData, ([key, values]) => [
        key,
        values.filter(
          (d) => d.marketDate >= startDate && d.marketDate <= endDate,
        ),
      ]);

      currentSeriesByKey = new Map(filteredData);
      tooltipDates = Array.from(
        new Set(filteredData.flatMap(([, values]) => values.map((d) => +d.marketDate))),
        (value) => ({ marketDate: new Date(value) }),
      ).sort((a, b) => a.marketDate - b.marketDate);

      xScale.domain([startDate, endDate]);
      yScale
        .domain(
          getNumericExtent(
            filteredData.flatMap(([, values]) =>
              values.map((d) => d.settlePrice),
            ),
          ),
        )
        .nice();

      const t = d3.transition().duration(50).ease(d3.easeLinear);

      xAxis.transition(t).call(d3.axisBottom(xScale));
      yAxis
        .transition(t)
        .call(d3.axisLeft(yScale).tickFormat((d) => `$${d.toFixed(2)}`));
      gridlines
        .transition(t)
        .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

      filteredData.forEach(([key, values]) => {
        const series = seriesPaths.get(key);
        series.areaPath.datum(values).transition(t).attr("d", series.area);
        series.linePath.datum(values).transition(t).attr("d", line);
      });
    };

    const throttledUpdateChart = throttle(updateChart, 30);

    const slider = sliderBottom()
      .min(d3.min(normalizedData, (d) => d.marketDate))
      .max(d3.max(normalizedData, (d) => d.marketDate))
      .width(sliderWidth)
      .tickFormat(d3.timeFormat("%Y-%m-%d"))
      .ticks(5)
      .default([
        initialStartDate || d3.min(normalizedData, (d) => d.marketDate),
        initialEndDate || d3.max(normalizedData, (d) => d.marketDate),
      ])
      .fill("var(--theme-interactive-element)")
      .on("onchange", throttledUpdateChart);

    sliderSvg
      .append("g")
      .attr("transform", `translate(${(parentWidth - sliderWidth) / 2},0)`)
      .call(slider);

    sliderSvg.selectAll(".slider text").style("fill", "grey");
    sliderSvg.selectAll(".tick text").style("opacity", "0");

    updateChart([
      initialStartDate || d3.min(normalizedData, (d) => d.marketDate),
      initialEndDate || d3.max(normalizedData, (d) => d.marketDate),
    ]);
  }, [data, title, initialStartDate, initialEndDate]);

  const throttle = (func, limit) => {
    let lastFunc;
    let lastRan;
    return function () {
      const context = this;
      const args = arguments;
      if (!lastRan) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(
          function () {
            if (Date.now() - lastRan >= limit) {
              func.apply(context, args);
              lastRan = Date.now();
            }
          },
          limit - (Date.now() - lastRan),
        );
      }
    };
  };

  useEffect(() => {
    if (!isLoading) {
      renderChart();
    }
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current;
    if (!svgNode) return;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;

  return (
    <div className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}>
      <svg ref={svgRef} style={{ width: "100%" }}></svg>
      <svg ref={sliderRef} style={{ width: "100%" }}></svg>
    </div>
  );
};

export default HistoricalSettlementsGraph;








